import * as React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { Slot as SlotPrimitive } from 'radix-ui'

import { cn } from '@/lib/utils/index'

import { buttonVariants } from './button-variants'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  // In-app buttons take the subtle rounded-md radius (STYLE_GUIDE.md). `pill` is the opt-in
  // for marketing surfaces (pricing cards) that mirror orderx.com's rounded CTAs; `square`
  // is for a button that must sit flush in a square container.
  shape?: 'pill' | 'square'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? SlotPrimitive.Slot : 'button'
    // Shape is owned here, not by call sites, so every button gets the same corners (a
    // call site's own `rounded-*` is overridden). Ghost icon buttons stay circular; an
    // explicit shape always wins.
    const shapeClass =
      shape === 'pill'
        ? 'rounded-full'
        : shape === 'square'
          ? 'rounded-none'
          : variant === 'ghost' && size === 'icon'
            ? 'rounded-full'
            : 'rounded-md'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), shapeClass)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button }
